/**
 * Утилиты скачивания Suno-треков: MP3 (всегда доступен из audio_url) и WAV
 * (конвертируется первый раз, потом кэшируется в suno_tracks.wav_url).
 */
import { convertToWav, type SunoTrack } from '../../../api/music'
import { waitForTask } from '../../generate/utils/waitForTask'
import { showToast } from '../../../components/Toast'
import { downloadAudioWithCover, safeFilename } from './audioWithCover'

/**
 * Скачивает файл по URL через blob (корректно работает в Safari и на
 * iOS через share sheet).
 */
export async function downloadFromUrl(url: string, filename: string): Promise<void> {
	try {
		const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent)
		if (isIOS) showToast('Подготовка файла...', 'info')

		const response = await fetch(url)
		if (!response.ok) throw new Error(`HTTP ${response.status}`)
		const blob = await response.blob()

		if (isIOS) {
			const shareFile = new File([blob], filename, { type: blob.type })
			if (navigator.canShare?.({ files: [shareFile] })) {
				try {
					await navigator.share({ files: [shareFile] })
				} catch (e) {
					if (e instanceof Error && e.name !== 'AbortError') throw e
				}
				return
			}
			window.open(url, '_blank')
			return
		}

		const objectUrl = URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = objectUrl
		a.download = filename
		document.body.appendChild(a)
		a.click()
		a.remove()
		setTimeout(() => URL.revokeObjectURL(objectUrl), 10000)
	} catch {
		showToast('Не удалось скачать файл', 'error')
	}
}

/** Скачать MP3 с embedded cover (если есть image_url). */
export async function downloadMp3(track: SunoTrack): Promise<void> {
	const url = track.audio_url || track.stream_audio_url
	if (!url) {
		showToast('У трека нет аудио-файла', 'error')
		return
	}
	await downloadAudioWithCover(url, track.image_url || null, safeFilename(track.title, 'mp3'), {
		title: track.title,
		artist: 'Umnik AI',
		genre: track.tags,
	})
}

const POLL_INTERVAL_MS = 10_000 // WAV обычно готов за 10–30 с

/**
 * Скачать WAV. Если `track.wav_url` уже есть — сразу скачиваем (бесплатно).
 * Иначе запускаем конвертацию и ждём завершения, после чего скачиваем и
 * вызываем `onCached` чтобы родитель мог обновить свой кэш трека.
 *
 * Возвращает финальный WAV URL либо null при ошибке/отмене.
 */
export async function downloadWav(
	track: SunoTrack,
	opts: { onCached?: (wavUrl: string) => void } = {},
): Promise<string | null> {
	// Cached — мгновенный download без запросов.
	if (track.wav_url) {
		await downloadAudioWithCover(track.wav_url, track.image_url || null, safeFilename(track.title, 'wav'), {
			title: track.title,
			artist: 'Umnik AI',
			genre: track.tags,
		})
		return track.wav_url
	}

	// Convert: запускаем + опрашиваем статус, потом качаем.
	showToast('Конвертация в WAV — запущена', 'info')
	const res = await convertToWav({ track_id: track.id })
	if (!res.ok || !res.request_id) {
		showToast(res.error || 'Не удалось запустить конвертацию', 'error')
		return null
	}

	// Ждём ответа сервера без потолка (waitForTask, план «Ждём ответа нейросети», Ф3): прежние 3 минуты
	// объявляли «Таймаут конвертации», пока Suno досчитывал.
	const s = await waitForTask(res.request_id, { minDelayMs: POLL_INTERVAL_MS })
	if (!s || s.status === 'failed') {
		showToast(s?.error_message_ru || s?.error_message || 'Конвертация не удалась', 'error')
		return null
	}
	const wavUrl = s.result_urls?.[0] || s.result_url
	if (!wavUrl) {
		showToast('Конвертация завершилась без URL', 'error')
		return null
	}
	opts.onCached?.(wavUrl)
	await downloadAudioWithCover(wavUrl, track.image_url || null, safeFilename(track.title, 'wav'), {
		title: track.title,
		artist: 'Umnik AI',
		genre: track.tags,
	})
	showToast('WAV готов', 'success')
	return wavUrl
}
